import { useCallback, useEffect, useState } from "react";
import { Loader2, Folder, FileText, Film, Image as ImageIcon, ChevronRight, Home, X, CloudOff, Cloud, History } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtSize } from "@/pages/Dateien";
import { useAccount } from "@/context/AccountContext";

function PickIcon({ item }) {
  if (item.is_folder) return <Folder className="h-5 w-5 text-sky-600 shrink-0" />;
  if ((item.mime || "").startsWith("image/")) return <ImageIcon className="h-5 w-5 text-emerald-600 shrink-0" />;
  if ((item.mime || "").startsWith("video/")) return <Film className="h-5 w-5 text-violet-600 shrink-0" />;
  return <FileText className="h-5 w-5 text-neutral-400 shrink-0" />;
}

export function SharePointPickerDialog({ open, onClose, onPicked, projectId = "", pickFolder = false, onPickedFolder }) {
  const { currentKey, current } = useAccount() || {};
  const [status, setStatus] = useState(null);
  const [drive, setDrive] = useState(null);
  const [crumbs, setCrumbs] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState("");
  const [recents, setRecents] = useState([]);

  const browse = useCallback(async (driveId, itemId = null) => {
    setLoading(true);
    try {
      const { data } = await api.get("/sharepoint/browse", { params: { drive_id: driveId, account: currentKey, ...(itemId ? { item_id: itemId } : {}) } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "SharePoint-Anfrage fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [currentKey]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCrumbs([]);
    setItems([]);
    api.get("/sharepoint/recent-folders", { params: { account: currentKey } }).then(({ data }) => setRecents(data)).catch(() => {});
    (async () => {
      setLoading(true);
      try {
        const { data: st } = await api.get("/sharepoint/status", { params: { account: currentKey } });
        if (cancelled) return;
        setStatus(st);
        if (!st.connected) { setLoading(false); return; }
        const { data: drv } = await api.get("/sharepoint/drives", { params: { account: currentKey } });
        if (cancelled || !drv.length) { setLoading(false); return; }
        setDrive(drv[0]);
        await browse(drv[0].id);
      } catch {
        if (!cancelled) { setStatus({ connected: false }); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [open, browse, currentKey]);

  if (!open) return null;

  const openFolder = (item) => {
    setCrumbs((c) => [...c, { id: item.id, name: item.name }]);
    browse(drive.id, item.id);
  };

  const jumpToRecent = (r) => {
    setCrumbs([{ id: r.folder_id, name: r.name }]);
    browse(r.drive_id, r.folder_id);
  };

  const goCrumb = (idx) => {
    if (idx < 0) { setCrumbs([]); browse(drive.id); }
    else {
      const next = crumbs.slice(0, idx + 1);
      setCrumbs(next);
      browse(drive.id, next[next.length - 1].id);
    }
  };

  const pick = async (item) => {
    setImporting(item.id);
    try {
      const { data } = await api.post("/sharepoint/import", { drive_id: drive.id, item_id: item.id, project_id: projectId, account_key: currentKey });
      const folder = crumbs[crumbs.length - 1];
      if (folder) api.post("/sharepoint/recent-folders", { drive_id: drive.id, folder_id: folder.id, name: folder.name, account_key: currentKey }).catch(() => {});
      onPicked({ file_id: data.id, url: data.url, kind: data.kind, filename: data.filename });
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Übernahme fehlgeschlagen");
    } finally {
      setImporting("");
    }
  };

  return (
    <div className="fixed inset-0 z-[10006] flex items-end sm:items-center justify-center" data-testid="sharepoint-picker-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[75vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
          <div className="text-sm font-bold flex items-center gap-2 text-neutral-900">
            <Cloud className="h-4 w-4 text-sky-600" /> {pickFolder ? "SharePoint-Ordner wählen" : "Aus SharePoint anhängen"}
          </div>
          <button data-testid="sharepoint-picker-close" onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-500">
            <X className="h-4 w-4" />
          </button>
        </div>

        {status && !status.connected ? (
          <div className="text-center py-10 text-neutral-400">
            <CloudOff className="h-7 w-7 mx-auto mb-2" />
            <p className="text-sm">
              {status.configured ? "SharePoint ist derzeit nicht verbunden." : `Für ${current?.name || currentKey} ist keine SharePoint-Quelle hinterlegt.`}
            </p>
            {status.error && <p className="text-xs mt-1 max-w-sm mx-auto">{status.error}</p>}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1 text-xs text-neutral-500 flex-wrap px-3 py-2 border-b border-neutral-50">
              <button onClick={() => goCrumb(-1)} data-testid="sharepoint-picker-crumb-root"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-neutral-100 font-semibold">
                <Home className="h-3.5 w-3.5" /> {drive?.name || "Dokumente"}
              </button>
              {crumbs.map((c, i) => (
                <span key={c.id} className="inline-flex items-center gap-1">
                  <ChevronRight className="h-3 w-3 text-neutral-300" />
                  <button onClick={() => goCrumb(i)} className="px-2 py-1 rounded-lg hover:bg-neutral-100 font-medium truncate max-w-[110px]">{c.name}</button>
                </span>
              ))}
            </div>
            {recents.length > 0 && crumbs.length === 0 && (
              <div className="flex items-center gap-1.5 px-3 py-2 border-b border-neutral-50 overflow-x-auto" data-testid="sharepoint-recent-folders">
                <History className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                {recents.map((r) => (
                  <button
                    key={`${r.drive_id}-${r.folder_id}`}
                    data-testid={`sharepoint-recent-${r.folder_id}`}
                    onClick={() => jumpToRecent(r)}
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-[11px] font-semibold text-sky-700 hover:bg-sky-100"
                  >
                    <Folder className="h-3 w-3" /> {r.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-1.5">
              {loading ? (
                <div className="py-10 flex justify-center text-neutral-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : (pickFolder ? items.filter((i) => i.is_folder) : items).length === 0 ? (
                <p className="text-sm text-neutral-400 text-center py-8">{pickFolder ? "Keine Unterordner vorhanden." : "Dieser Ordner ist leer."}</p>
              ) : (
                (pickFolder ? items.filter((i) => i.is_folder) : items).map((item) => (
                  <button
                    key={item.id}
                    data-testid={`sharepoint-pick-${item.id}`}
                    disabled={!!importing}
                    onClick={() => (item.is_folder ? openFolder(item) : pick(item))}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neutral-50 text-left disabled:opacity-50"
                  >
                    {item.thumb ? (
                      <img src={item.thumb} alt="" loading="lazy" className="h-9 w-9 rounded-lg object-cover shrink-0 border border-neutral-100" />
                    ) : (
                      <PickIcon item={item} />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-neutral-800 truncate">{item.name}</p>
                      <p className="text-[11px] text-neutral-400">
                        {item.is_folder ? `${item.child_count} ${item.child_count === 1 ? "Element" : "Elemente"}` : fmtSize(item.size)}
                      </p>
                    </div>
                    {item.is_folder ? (
                      <ChevronRight className="h-4 w-4 text-neutral-300 shrink-0" />
                    ) : importing === item.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-sky-600 shrink-0" />
                    ) : (
                      <span className="text-[11px] font-semibold text-sky-600 shrink-0">Anhängen</span>
                    )}
                  </button>
                ))
              )}
            </div>
            {pickFolder ? (
              <div className="px-3 py-2.5 border-t border-neutral-100">
                <button
                  data-testid="sharepoint-pick-current-folder"
                  disabled={!drive || loading}
                  onClick={() => {
                    const cur = crumbs[crumbs.length - 1];
                    onPickedFolder({ drive_id: drive.id, folder_id: cur?.id || "", name: cur?.name || drive?.name || "Dokumente" });
                  }}
                  className="w-full py-2.5 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 transition"
                >
                  Diesen Ordner wählen{crumbs.length ? `: „${crumbs[crumbs.length - 1].name}“` : ""}
                </button>
              </div>
            ) : (
              <p className="px-4 py-2 text-[10px] text-neutral-400 border-t border-neutral-50">
                Die Datei wird in die Ablage übernommen und als Anhang eingefügt (max. 250 MB).
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
